import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Loader2 } from 'lucide-react'
import { useUIStore } from '@stores'
import { useSessionStore } from '@stores/useSessionStore'
import ProcessingGif from '@components/ProcessingGif'
import ProcessingLoader from '@components/ProcessingLoader'
import { postMessageToParent } from '@utils/iframe'
import { OVERLAY_LOADING_Z } from '@utils/overlayStack'

export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  fullScreen?: boolean
  message?: string
  useProcessingGif?: boolean
}

const LoadingSpinner = ({
  size = 'md',
  className = '',
  fullScreen = false,
  message,
  useProcessingGif = false,
}: LoadingSpinnerProps) => {
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20',
  }

  // Use processing GIF if requested and available
  if (useProcessingGif) {
    return (
      <div
        className={fullScreen ? 'fixed inset-0 flex items-center justify-center z-50' : ''}
        style={
          fullScreen
            ? {
                backgroundColor: 'color-mix(in srgb, var(--content-background-color) 90%, transparent)',
              }
            : undefined
        }>
        <ProcessingGif className="w-32 h-32 object-contain" processing />
      </div>
    )
  }

  const spinner = (
    <div className={`${sizeClasses[size]} ${className}`}>
      <Loader2
        className="animate-spin h-full w-full"
        style={{ color: 'var(--theme-color)' }}
        role="status"
        aria-label="Loading"
      />
    </div>
  )

  if (fullScreen) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center z-50"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--content-background-color) 75%, transparent)',
        }}>
        <div className="text-center max-w-sm mx-auto p-8">
          <div className="flex justify-center mb-4">{spinner}</div>
          <p className="text-gray-600 text-lg">{message || 'Loading...'}</p>
        </div>
      </div>
    )
  }

  return spinner
}

export interface InitialLoadingProps {
  isLoading: boolean
  className?: string
  message?: string
}

export const InitialLoading = ({ isLoading, className = '', message: _message }: InitialLoadingProps) => {
  if (!isLoading) {
    return null
  }

  return (
    <div
      className={`flex items-center justify-center ${className}`}
      style={{ minHeight: 'max(100vh, 480px)', backgroundColor: 'var(--content-background-color)' }}>
      <LoadingSpinner size="xl" useProcessingGif />
    </div>
  )
}

/**
 * Popup loading overlay for specific actions
 */
export interface PopupLoadingProps {
  isLoading: boolean
  message?: string
  className?: string
  transparent?: boolean
}

export const PopupLoading = ({ isLoading, message, className = '', transparent = false }: PopupLoadingProps) => {
  const { isPopupLoading } = useUIStore()

  if (!isLoading && !isPopupLoading) {
    return null
  }

  const bgStyle = transparent
    ? {
        backgroundColor: 'color-mix(in srgb, var(--content-background-color) 60%, transparent)',
      }
    : { backgroundColor: 'rgba(0,0,0,0.4)' }

  return (
    <div className={`absolute inset-0 flex items-center justify-center z-30 ${className}`} style={bgStyle}>
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <ProcessingLoader width={60} height={60} />
        </div>
        {message && <p className="text-gray-800 font-medium">{message}</p>}
      </div>
    </div>
  )
}

/**
 * Global loading overlay — renders inside <main> (position: absolute).
 * Header and footer stay visible. Uses ProcessingGif (campaign image + rotating messages)
 * wrapped in a white card for dark mode compatibility. Falls back to Loader2 spinner.
 */
export const GlobalLoading = () => {
  const { isLoading, forceDisplayLoader } = useUIStore()
  const session = useSessionStore(s => s.session)
  const hasGif = !!session?.campaign?.processing_gif?.file
  const visible = isLoading || forceDisplayLoader

  useEffect(() => {
    if (visible) {
      postMessageToParent({ type: 'scrollIframeCenter' })
      return () => {
        // For center modal, don't scroll to top in iframe - let the modal handle its own positioning
        if (document.querySelector('[role="dialog"]')) {
          return
        }
        postMessageToParent({ type: 'scrollToTop' })
      }
    }
  }, [visible])

  if (!visible) {
    return null
  }

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center backdrop-blur-sm"
      style={{
        zIndex: OVERLAY_LOADING_Z,
        backgroundColor: 'color-mix(in srgb, var(--content-background-color) 80%, transparent)',
      }}>
      {hasGif ? (
        <div className="processing-gif-card">
          <ProcessingGif className="processing-gif-img" processing={visible} />
        </div>
      ) : (
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin" style={{ color: 'var(--theme-color)' }} />
          <p className="text-base font-medium" style={{ color: 'var(--text-color)' }}>
            Processing...
          </p>
        </div>
      )}
    </div>,
    document.body
  )
}

export default LoadingSpinner
